/**
 * @module public/js/utils/tenantLifecycle
 * @summary Shared browser helpers for tenant lifecycle state.
 */

/**
 * Canonical lifecycle states surfaced to browser admin tooling.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const TENANT_LIFECYCLE_STATUS = Object.freeze({
  ACTIVE: 'active',
  QUARANTINED: 'quarantined',
  PURGE_RUNNING: 'purge_running',
  PURGED: 'purged',
});

/**
 * Normalize an arbitrary lifecycle value into a supported state.
 *
 * Missing or unknown values intentionally default to `active` so legacy tenant
 * root docs continue to behave as live tenants.
 *
 * @param {unknown} value - Candidate lifecycle value.
 * @returns {string} Supported lifecycle state.
 */
export function normalizeTenantLifecycleStatus(value) {
  const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
  const allowed = Object.values(TENANT_LIFECYCLE_STATUS);
  return allowed.includes(candidate)
    ? candidate
    : TENANT_LIFECYCLE_STATUS.ACTIVE;
}

/**
 * Read the normalized lifecycle state from a tenant root payload.
 *
 * @param {Record<string, any>|null|undefined} data - Tenant root document data.
 * @returns {string} Normalized lifecycle state.
 */
export function getTenantLifecycleStatus(data) {
  const lifecycle =
    data &&
    typeof data === 'object' &&
    data.lifecycle &&
    typeof data.lifecycle === 'object'
      ? data.lifecycle
      : {};
  return normalizeTenantLifecycleStatus(lifecycle.status);
}

/**
 * Determine whether a tenant should appear in active admin tooling.
 *
 * @param {Record<string, any>|null|undefined} data - Tenant root document data.
 * @returns {boolean} True when the tenant is active.
 */
export function isTenantActive(data) {
  return getTenantLifecycleStatus(data) === TENANT_LIFECYCLE_STATUS.ACTIVE;
}
